import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from './Auth';
import { useCatalogStatus } from './Catalog';

/**
 * Streaming services as a way of browsing, not a filter among many.
 *
 * The list is the region's own, from /api/catalog/providers (TMDB watch
 * providers, ranked by the region's display priority). It arrives padded with
 * things nobody means by "a service": rent-and-buy stores, add-on channels
 * sold through another service, ad tiers of one already listed. Browsing
 * filters to what is included with a subscription, so a store would always
 * come back nearly empty - they are dropped here, and the same service under
 * two names is kept once.
 */

const NOT_A_SERVICE = /store|amazon video|rakuten|google play|youtube|microsoft|amazon channel|apple tv channel|roku channel|with ads|justwatch/i;

// What people call them, not how the catalog spells them
const SHORT_NAMES = {
  'Amazon Prime Video': 'Prime Video',
  'Disney Plus': 'Disney+',
  'Paramount Plus': 'Paramount+',
  'Paramount Plus Premium': 'Paramount+',
  'Apple TV Plus': 'Apple TV+',
  'Peacock Premium': 'Peacock',
  'Peacock Premium Plus': 'Peacock',
  'Tubi TV': 'Tubi',
  'Max Amazon Channel': 'Max',
  'Discovery Plus': 'discovery+'
};

export const shortName = (name) => SHORT_NAMES[name] || name;

// One request per page load; the list changes daily at most
let listing = null;
const fetchServices = () => {
  if (!listing) {
    listing = axios
      .get('/api/catalog/providers')
      .then(({ data }) => data.data || { providers: [] })
      .catch(() => {
        listing = null;
        return { providers: [] };
      });
  }
  return listing;
};

const regionName = (code) => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code);
  } catch (_) {
    return code;
  }
};

/**
 * The region's services, cleaned up for browsing.
 *
 * `services` is the switcher's list; `all` is everything the region named, so
 * a link that points at a store (an old bookmark, a home row) still has a name
 * to show.
 */
export const useServices = () => {
  const { ready, supportsFilter } = useCatalogStatus();
  const usable = ready && supportsFilter('provider');
  const [state, setState] = useState({ loading: true, all: [], region: '' });

  useEffect(() => {
    if (!usable) {
      setState({ loading: !ready, all: [], region: '' });
      return undefined;
    }
    let cancelled = false;
    fetchServices().then((data) => {
      if (!cancelled) setState({ loading: false, all: data.providers || [], region: data.region || '' });
    });
    return () => {
      cancelled = true;
    };
  }, [ready, usable]);

  return useMemo(() => {
    const seen = new Set();
    const services = [];
    for (const row of state.all) {
      if (NOT_A_SERVICE.test(row.name)) continue;
      const name = shortName(row.name);
      if (seen.has(name)) continue;
      seen.add(name);
      services.push({ ...row, name });
    }
    const all = state.all.map((row) => ({ ...row, name: shortName(row.name) }));
    return {
      loading: state.loading,
      services,
      all,
      region: state.region,
      regionName: state.region ? regionName(state.region) : '',
      find: (id) => all.find((row) => String(row.id) === String(id)) || null
    };
  }, [state]);
};

const CHANGED = 'browse-service-changed';

/**
 * The service this profile is browsing, kept until it is cleared.
 *
 * Stored per account and profile in this browser, so it follows the viewer
 * from Home to Films to Series and survives a reload, and two people sharing
 * an account through different profiles each keep their own. Every mounted
 * hook hears a change at once through a window event; another tab hears it
 * through `storage`.
 */
export const useBrowseService = () => {
  const { user } = useAuth();
  const key = user ? `browse.service:${user.username}:${user.profile?.id || ''}` : null;

  const read = useCallback(() => {
    if (!key) return '';
    try {
      return localStorage.getItem(key) || '';
    } catch (_) {
      return '';
    }
  }, [key]);

  const [value, setValue] = useState(read);

  useEffect(() => {
    setValue(read());
    const sync = () => setValue(read());
    window.addEventListener(CHANGED, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGED, sync);
      window.removeEventListener('storage', sync);
    };
  }, [read]);

  const set = useCallback(
    (next) => {
      if (!key) return;
      try {
        if (next) localStorage.setItem(key, String(next));
        else localStorage.removeItem(key);
      } catch (_) {
        // Private mode: the choice still holds for this page, just not past it
      }
      setValue(next ? String(next) : '');
      window.dispatchEvent(new Event(CHANGED));
    },
    [key]
  );

  return [value, set];
};

/** The shelves Home shows while a service is chosen, all scoped to it. */
const SERVICE_ROWS = [
  { key: 'trending', title: (name) => `Trending on ${name}`, note: 'films & series', query: 'type=all&sort=trending' },
  { key: 'films', title: () => 'Films', note: 'most popular', query: 'type=movie&sort=trending' },
  { key: 'series', title: () => 'Series', note: 'most popular', query: 'type=series&sort=trending' },
  { key: 'top', title: () => 'Top rated', note: 'all time', query: 'type=all&sort=top_rated' },
  { key: 'recent', title: () => 'Recently released', note: 'newest first', query: 'type=all&sort=recent' }
];

/**
 * Home's hero and rows for one service, fetched together.
 *
 * Returns null when no service is chosen, so Home falls back to the catalog's
 * own page. The hero is the service's top trending title that has a backdrop
 * to show.
 */
export const useServiceHome = (serviceId, service) => {
  const [state, setState] = useState({ for: '', loading: false, hero: null, rows: [] });

  useEffect(() => {
    if (!serviceId) return undefined;
    let cancelled = false;
    setState({ for: serviceId, loading: true, hero: null, rows: [] });

    Promise.all(
      SERVICE_ROWS.map((row) =>
        axios
          .get(`/api/catalog/discover?${row.query}&provider=${serviceId}`)
          .then(({ data }) => data.data?.items || [])
          .catch(() => [])
      )
    ).then((results) => {
      if (cancelled) return;
      const hero = (results[0] || []).find((item) => item.backdrop) || null;
      setState({
        for: serviceId,
        loading: false,
        hero,
        rows: SERVICE_ROWS.map((row, index) => ({
          key: `service-${row.key}`,
          baseTitle: row.title,
          note: row.note,
          query: `${row.query}&provider=${serviceId}`,
          items: results[index]
        })).filter((row) => row.items.length)
      });
    });

    return () => {
      cancelled = true;
    };
  }, [serviceId]);

  const name = service?.name || 'this service';
  return useMemo(() => {
    if (!serviceId) return null;
    const current = state.for === serviceId ? state : { loading: true, hero: null, rows: [] };
    return {
      loading: current.loading,
      hero: current.hero,
      rows: current.rows.map(({ baseTitle, ...row }) => ({ ...row, title: baseTitle(name) }))
    };
  }, [serviceId, state, name]);
};
